import {
  Box, TextField, InputAdornment, MenuItem, Checkbox, ListItemText,
  Select, FormControl, InputLabel, OutlinedInput, Chip, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';

export interface ProjectFilter {
  search: string;
  states: string[];
  priority: string;
}

export const EMPTY_PROJECT_FILTER: ProjectFilter = { search: '', states: [], priority: '' };

const STATES = ['PLANNED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];
const PRIORITIES = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];

/** True, wenn das Projekt zum gemeinsamen Filter passt (clientseitig). */
export function matchesProjectFilter(
  p: { name: string; state: string; priority: string },
  f: ProjectFilter,
): boolean {
  if (f.search.trim() && !p.name.toLowerCase().includes(f.search.trim().toLowerCase())) return false;
  if (f.states.length > 0 && !f.states.includes(p.state)) return false;
  if (f.priority && p.priority !== f.priority) return false;
  return true;
}

/**
 * Gemeinsame Filterleiste für die Programm-HQ-Seite — filtert Timeline-Balken
 * UND Projekttabelle gleichzeitig (Suche / Status / Priorität).
 */
export function ProjectFilterBar({ value, onChange }: { value: ProjectFilter; onChange: (next: ProjectFilter) => void }) {
  const { t } = useTranslation('projects');
  const active = value.search.trim() !== '' || value.states.length > 0 || value.priority !== '';

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
      <TextField
        size="small"
        placeholder={t('project.searchPlaceholder', 'Projekt suchen …')}
        value={value.search}
        onChange={(e) => onChange({ ...value, search: e.target.value })}
        sx={{ minWidth: 220 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>{t('project.state', 'Status')}</InputLabel>
        <Select
          multiple
          value={value.states}
          onChange={(e) => onChange({ ...value, states: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
          input={<OutlinedInput label={t('project.state', 'Status')} />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(selected as string[]).map((s) => <Chip key={s} size="small" label={t(`states.${s}`, s)} />)}
            </Box>
          )}
        >
          {STATES.map((s) => (
            <MenuItem key={s} value={s}>
              <Checkbox size="small" checked={value.states.includes(s)} />
              <ListItemText primary={t(`states.${s}`, s)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>{t('project.priority', 'Priorität')}</InputLabel>
        <Select
          value={value.priority}
          label={t('project.priority', 'Priorität')}
          onChange={(e) => onChange({ ...value, priority: e.target.value })}
        >
          <MenuItem value=""><em>{t('project.allPriorities', 'Alle')}</em></MenuItem>
          {PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>{t(`priorities.${p}`, p)}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {active && (
        <Button size="small" startIcon={<ClearIcon />} onClick={() => onChange(EMPTY_PROJECT_FILTER)}>
          {t('project.clearFilter', 'Filter zurücksetzen')}
        </Button>
      )}
    </Box>
  );
}
